import LightSource from "./LightSource";
import GridTile from "../grid/GridTile";
import PreciseShadowCaster from "./PreciseShadowCaster";
import GridInversionOfControl from "../grid/GridInversionOfControl";
import { Settings } from "../../Settings";
import GridRegion from "../grid/GridRegion";
import DynamicLightSourceCastCallback from "./DynamicLightSourceCastCallback";
import PositionalLightSourceCastCallback from "./PositionalLightSourceCastCallback";
import EnvironmentalLightSourceCastCallback from "./EnvironmentLightSourceCastCallback";
import Color from "../color/Color";
import Projection from "../../Projection";
import WorldGrid from "../grid/WorldGrid";
import Grid from "../grid/Grid";
import GameModel from "../../GameModel";

export default class LightingCalculator extends GameModel {
    /**
     * @param {WorldGrid} worldGrid
     * @param {Projection} projection 
     */
    constructor(worldGrid, projection) {
        super();

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;

        /**
         * @constant
         * @type {Projection}
         */
        this.projection = projection;

        /**
         * @constant
         * @type {number}
         */
        this.visibleTileRadius = 13;

        /**
         * @constant
         * @type {number}
         */
        this.lightSourceRadius = 7; // 6

        /**
         * @constant
         * @type {number}
         */
        this.mainPositionalTileRadius = 7;
        /**
         * @constant
         * @type {number}
         */
        this.secondaryPositionalTileRadius = 5;

        /**
         * @constant
         * @type {number}
         */
        this.lightSourceRecalculationFrames = 40;

        /**
         * @constant
         * @type {number}
         */
        this.maxDynamicLightSources = 10;

        /**
         * @type {number}
         */
        this.elapsedFrameCount = this.lightSourceRecalculationFrames;

        /**
         * @type {Array.<LightSource>}
         */
        this.dynamicLightSources = [];
    
        /**
         * Used to keep track of last tile used for positional lighting.
         * @type {GridTile}
         */
        this.calculatedTile = null;

        /**
         * @type {PreciseShadowCaster}
         */
        this.shadowCaster = new PreciseShadowCaster(worldGrid);

        /**
         * @type {boolean}
         */
        this.forceEnvironmentalRecalculation = false;

        /**
         * @constant
         * @type {DynamicLightSourceCastCallback}
         */
        this.dynamicLightSourceCastCallback = new DynamicLightSourceCastCallback(new Color(0, 0, 0, 0), 5);

        /**
         * @constant
         * @type {PositionalLightSourceCastCallback}
         */
        this.positionalLightSourceCastCallback = new PositionalLightSourceCastCallback(new Color(0, 0, 0, 1), this.mainPositionalTileRadius);

        /**
         * @constant
         * @type {PositionalLightSourceCastCallback}
         */
        this.secondaryPositionalLightSourceCastCallback = new PositionalLightSourceCastCallback(new Color(0, 0, 0, 1), this.secondaryPositionalTileRadius);

        /**
         * @constant
         * @type {EnvironmentalLightSourceCastCallback}
         */
        this.environmentalLightSourceCastCallback = new EnvironmentalLightSourceCastCallback(new Color(0, 0, 0, 0), 5);

        /**
         * @type {Function}
         * @param {GridRegion} region 
         */
        this.regionResetLightingCallback = (region) => {
            const tiles = region.getTiles();
            for (let i = 0; i < tiles.length; i++) {
                tiles[i].lighting.resetLighting();
            }
        };
        
        const thisRef = this;

        /**
         * @type {Function}
         * @param {Grid} grid 
         */
        this.gridEnvironmentalLightingCallback = (grid) => {
            const lightSources = grid.getLightSources();
            if (!lightSources || lightSources.length === 0) {
                return;
            }

            /////////////////////////////////////////////////
            // Cast light from every light source.
            /////////////////////////////////////////////////

            for (let i = 0; i < lightSources.length; i++) {
                const lightSource = lightSources[i];
                if (lightSource.isFinishedAndAvailable()) {
                    continue;
                }

                const castRadius = lightSource.getTileRadius();
                thisRef.environmentalLightSourceCastCallback.setValues(lightSource.getColor(), castRadius)
                thisRef.shadowCaster.castShadows(lightSource.getTile(), castRadius, thisRef.environmentalLightSourceCastCallback);
            }
        };
    }

    resetLightingCalculator() {
        this.dynamicLightSources.length = 0;
    }

    /**
     * @param {LightSource} lightSource 
     */
    addDynamicLightSource(lightSource) {
        if (!lightSource) {
            return;
        }
        
        if (lightSource.getTile() == null || lightSource.isFinishedAndAvailable()) {
            return;
        }

        this.dynamicLightSources.push(lightSource);
        
        // Prevent there being too many lights being rendered.
        const toRemove = this.dynamicLightSources.length - this.maxDynamicLightSources;
        if (toRemove > 0) {
            this.dynamicLightSources.splice(this.maxDynamicLightSources - 1, toRemove);
        }

        console.log('LightingCalculator.addDynamicLightSource() size=' + this.dynamicLightSources.length);
    }
    
    /**
     * @param {LightSource} lightSource 
     */
    removeDynamicLightSource(lightSource) {
        if (!lightSource) {
            return;
        }

        const index = this.dynamicLightSources.indexOf(lightSource);
        if (index === -1) {
            return;
        }

        this.dynamicLightSources.splice(index, 1);
    }

    resetForFrame() {
        GridInversionOfControl.allVisibleRegions(this.worldGrid, this.projection, this.regionResetLightingCallback);
    }

    /**
     * @private
     * @param {number} frameTimeRatio 
     */
    calculateEnvironmentLighting(frameTimeRatio) {
        // Some light sources (torches, candles) should appear to flicker. We need to
        // recalculate them periodically.
        this.elapsedFrameCount += frameTimeRatio;
        if (this.forceEnvironmentalRecalculation || this.elapsedFrameCount >= this.lightSourceRecalculationFrames) {
            this.elapsedFrameCount = 0;

            // // First we need to reset the environment lighting in all visible regions.
            // GridInversionOfControl.allVisibleRegions(state.worldGrid, state.projection, this.regionResetEnvironmentalLightingCallback);
            // Cast light from every light source in the visible regions.
            GridInversionOfControl.allVisibleGrids(this.worldGrid, this.projection, this.gridEnvironmentalLightingCallback);
        }
    }

    /**
     * @private
     */
    calculateDynamicLighting() {
        if (this.dynamicLightSources.length === 0) {
            return;
        }

        for (let i = this.dynamicLightSources.length - 1; i >= 0; i--) {
            const lightSource = this.dynamicLightSources[i];
            if (lightSource.isFinishedAndAvailable()) {
                this.dynamicLightSources.splice(i, 1);
                continue;
            }

            const tile = lightSource.getTile();
            if (!tile) {
                this.dynamicLightSources.splice(i, 1);
                continue;
            }

            // If the light source is too far away, then don't spend time calculating its effect.
            const radius = lightSource.getTileRadius();

            const lightOrigin = tile.getOrigin();
            if (!this.projection.isVisible(lightOrigin.x + Settings.tile.halfSize, lightOrigin.y + Settings.tile.halfSize, Settings.tile.size * radius)) {
                continue;
            }

            this.dynamicLightSourceCastCallback.setValues(lightSource.getColor(), radius);
            this.shadowCaster.castShadows(tile, radius, this.dynamicLightSourceCastCallback);
        }
    }

    /**
     * @param {GridTile} centerTile
     */
    calculatePrimaryPositionalLighting(centerTile) {
        if (!centerTile) {
            return;
        }

        /////////////////////////////////////////////////////
        // The main character acts as a light source.
        /////////////////////////////////////////////////////

        this.shadowCaster.castShadows(centerTile, this.mainPositionalTileRadius, this.positionalLightSourceCastCallback);
    }

    /**
     * @param {GridTile} centerTile
     */
    calculateSecondaryPositionalLighting(centerTile) {
        if (!centerTile) {
            return;
        }
        
        this.shadowCaster.castShadows(centerTile, this.secondaryPositionalTileRadius, this.secondaryPositionalLightSourceCastCallback);
    }

    onEnvironmentalLightSourceChange() {
        this.forceEnvironmentalRecalculation = true;
    }

    /**
     * Invoked when a wall tile has been removed. This lets the field of view calculator know that
     * things need to be recalculated.
     */
    onWallBroken() {
        this.forceRecalculation();
    }

    /**
     * @private
     */
    forceRecalculation() {
        //this.forcePositionalRecalculation = true;
        this.forceEnvironmentalRecalculation = true;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.dynamicLightSources.length = 0;
        this.calculatedTile = null;
        this.forceEnvironmentalRecalculation = false;
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }
}